import { colorsPalettes } from "@similarweb/styles";

import { CHART_COLORS } from "app/constants/ChartColors";
import { isNumberAndNotNan } from "app/filters/ngFilters";
import type { ISocialTrafficContext } from "app/pages/website-analysis/social-overview/socialTraffic/context";
import { timeGranularityList } from "app/pages/website-analysis/traffic-sources/search/tabs/SearchOverviewGraph/Helpers/SearchOverviewGraphConfig";
import DurationService from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";

interface ISocialMediaData {
    Share: number;
    Favicon: string;
}

type ISocialMedia = string;
type ISiteData = Record<ISocialMedia, ISocialMediaData>;
type ISite = string;
type IRawDataItems = Record<ISite, ISiteData>;

interface IRawData {
    Data: IRawDataItems;
}

interface IDataPoint {
    key: string;
    // display value
    y: number | null;
    color: string;
    share: number | null;
    favicon?: string | null;
}

type IDataPoints = Array<IDataPoint>;

interface IChartDataItem {
    name: string;
    data: IDataPoints;
}

export type IChartData = Array<IChartDataItem>;

const otherSocialMedias = {
    DISPLAY_NAME: "Other",
    COLOR: colorsPalettes.carbon[50],
};
export const getChartData = async (socialTrafficContext: ISocialTrafficContext): Promise<IChartData> => {
    try {
        const rawData = await fetchRawData(socialTrafficContext);
        const chartData = parseRawData(socialTrafficContext, rawData);
        return chartData;
    } catch {
        return Promise.resolve([]);
    }
};

const fetchRawData = (socialTrafficContext: ISocialTrafficContext): Promise<IRawData> => {
    const fetchService = DefaultFetchService.getInstance();
    const { params, apiController } = socialTrafficContext;
    const { country, key, isWWW, duration } = params;
    const ENDPOINT = `widgetApi/${apiController}Desktop/TrafficSourcesSocial/PieChart`;
    const { from, to, isWindow } = DurationService.getDurationData(duration).forAPI;
    const queryParams = {
        country,
        includeSubDomains: isWWW === "*",
        timeGranularity: timeGranularityList.monthly.name,
        from,
        to,
        isWindow,
        keys: key,
    };
    const rawData = fetchService.get<IRawData>(ENDPOINT, queryParams);
    return rawData;
};

const parseRawData = (socialTrafficContext: ISocialTrafficContext, rawData: IRawData): IChartData => {
    const { chosenSites } = socialTrafficContext;
    const isCompareMode = chosenSites.length > 1;
    const socialMedias = Object.keys(rawData.Data[chosenSites[0].name]);
    const chartData = chosenSites.map(({ name, color }) => {
        return {
            name,
            data: parserRawDataItem(name, color, rawData.Data[name], socialMedias, isCompareMode),
        };
    });
    return chartData;
};

const parserRawDataItem = (
    name: string,
    color: string,
    siteData: ISiteData,
    socialMedias: Array<string>,
    isCompareMode: boolean,
): IDataPoints => {
    let otherSocialsMediasShare = 1;
    const chartDataItem: IDataPoints = socialMedias.map((socialMedia, index) => {
        if (!siteData || !siteData[socialMedia]) {
            return {
                key: socialMedia,
                y: null,
                share: null,
                color,
                favicon: null,
            };
        }
        const { Share: share, Favicon: favicon } = siteData[socialMedia];
        const displayValue = share * 100;
        otherSocialsMediasShare = otherSocialsMediasShare - (isNumberAndNotNan(share) ? share : 0);
        return {
            key: socialMedia,
            y: displayValue,
            share,
            color: isCompareMode ? color : CHART_COLORS.chartMainColors[index],
            favicon,
        };
    });
    const otherSocialsMediasShareDisplayValue = otherSocialsMediasShare * 100;
    if (siteData) {
        chartDataItem.push({
            key: otherSocialMedias.DISPLAY_NAME,
            y: otherSocialsMediasShareDisplayValue,
            share: otherSocialsMediasShare,
            color: isCompareMode ? color : otherSocialMedias.COLOR,
            favicon: null,
        });
    }

    return chartDataItem;
};
